import type { HookAPI } from 'antd/es/modal/useModal'
import { useRef, useState } from 'react'
import type { UseFormGetValues, UseFormTrigger } from 'react-hook-form'
import { toast } from 'sonner'

import { handleApiError } from '@/utils/api/errors/handleApiError'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'

import { stockImportsApi } from '../../api/stockImportsApi'
import { STOCK_IMPORT_PROGRESS_MESSAGES } from '../../lib/stockImportsMessages'
import type { ValidationResponse } from '../../types/stockImports.types'
import type { ConfigSourceType } from '../lib/configFormModel'
import { type ConfigFormInput } from '../lib/configFormModel'
import { buildSourceValidationKey } from '../lib/configRequest'

export const SOURCE_VALIDATION_FIELDS: Partial<
  Record<ConfigSourceType, Array<keyof ConfigFormInput>>
> = {
  URL: ['url'],
  EMAIL: ['expectedSender', 'subjectMask', 'fileMask'],
  CUSTOM_IMAP: ['imapLogin', 'imapPasswordEncrypted', 'useSsl'],
}

const VALIDATION_PROGRESS: Partial<Record<ConfigSourceType, string>> = {
  URL: STOCK_IMPORT_PROGRESS_MESSAGES.validateUrl,
  EMAIL: 'Проверка настроек почты...',
  CUSTOM_IMAP: 'Проверка подключения...',
}

const requestSourceValidation = (
  values: ConfigFormInput,
  configId: string | null
) => {
  if (values.sourceType === 'URL')
    return stockImportsApi.validateUrl(values.url.trim())
  if (values.sourceType === 'EMAIL')
    return stockImportsApi.validateSystemEmail({
      expectedSender: values.expectedSender.trim(),
      subjectMask: values.subjectMask.trim(),
      fileMask: values.fileMask.trim() || undefined,
    })
  return stockImportsApi.validateEmail({
    imapHost: values.imapHost.trim() || null,
    imapPort: values.imapPort.trim() || null,
    imapLogin: values.imapLogin.trim(),
    imapPasswordRaw: values.imapPasswordEncrypted.trim(),
    useSsl: values.useSsl,
    configId,
  })
}

type ValidationState = {
  key: string | null
  response: ValidationResponse
}

type UseConfigSourceValidationParams = {
  sourceType: ConfigSourceType
  currentKey: string | null
  isMandatory: boolean
  configId: string | null
  modal: HookAPI
  getValues: UseFormGetValues<ConfigFormInput>
  trigger: UseFormTrigger<ConfigFormInput>
  rejectMaskedPassword: (values: ConfigFormInput) => boolean
}

export const useConfigSourceValidation = ({
  sourceType,
  currentKey,
  isMandatory,
  configId,
  modal,
  getValues,
  trigger,
  rejectMaskedPassword,
}: UseConfigSourceValidationParams) => {
  const [state, setState] = useState<ValidationState | null>(null)
  const [validating, setValidating] = useState(false)
  const requestVersionRef = useRef(0)
  const fields = SOURCE_VALIDATION_FIELDS[sourceType]
  const result = state && state.key === currentKey ? state.response : null
  const isValidated = !isMandatory || result?.success === true

  const validate = async () => {
    if (!fields) return
    if (!(await trigger(fields))) {
      handleFormSubmitError(false)
      return
    }
    const values = getValues()
    if (rejectMaskedPassword(values)) return

    const requestVersion = ++requestVersionRef.current
    const requestKey = buildSourceValidationKey(values)
    const isCurrent = () =>
      requestVersion === requestVersionRef.current &&
      requestKey === buildSourceValidationKey(getValues())
    setValidating(true)
    const toastId = toast.loading(VALIDATION_PROGRESS[sourceType])
    try {
      const response = await requestSourceValidation(values, configId)
      if (!isCurrent()) {
        toast.dismiss(toastId)
        return
      }
      setState({ key: requestKey, response })
      if (response.success) toast.success(response.message, { id: toastId })
      else toast.error(response.message, { id: toastId })
    } catch (error) {
      toast.dismiss(toastId)
      if (!isCurrent()) return
      setState(null)
      handleApiError(error, modal)
    } finally {
      if (requestVersion === requestVersionRef.current) setValidating(false)
    }
  }

  return {
    offersValidation: fields !== undefined,
    validating,
    result,
    isValidated,
    validate,
  }
}
